import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { Activity, Zap, DollarSign, Bot, Target, AlertTriangle, TrendingUp, BarChart3, Wrench, Download } from 'lucide-react'
import { Chart } from 'primereact/chart'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { Section } from '../../components/admin/ai/ui.js'

// Shared chart palette — brand blue, amber (warn), emerald (ok), muted grid.
const CHART_BLUE = '#3454cf'
const CHART_AMBER = '#f59e0b'
const CHART_EMERALD = '#10b981'
const CHART_GRID = 'rgba(0,0,0,0.06)'
const CHART_FONT = { family: 'inherit', size: 11 }

const BASE_CHART_OPTIONS = {
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' as const, labels: { font: CHART_FONT, boxWidth: 12 } } },
  scales: {
    x: { grid: { color: CHART_GRID }, ticks: { font: CHART_FONT } },
    y: { beginAtZero: true, grid: { color: CHART_GRID }, ticks: { font: CHART_FONT, precision: 0 } },
  },
}

interface OverviewData {
  range: string
  summary: {
    totalQueries: number
    avgLatencyMs: number
    totalInputTokens: number
    totalOutputTokens: number
    estimatedCostUsd: number
    activeAgentsCount: number
    trainingHitRate: number
    errorRate: number
    queryTrendPercent?: number
    costTrendPercent?: number
  }
  modelBreakdown: Array<{
    provider: string
    model: string
    queries: number
    avgLatencyMs: number
    costUsd: number
  }>
  channelUsage: Array<{
    channel: string
    percentage: number
    queries: number
  }>
  topFailingQueries: Array<{
    query: string
    count: number
    error: string
  }>
  latencyTimeSeries: Array<{
    timestamp: string
    avgLatencyMs: number
    queries: number
  }>
  agentBreakdown: Array<{
    agentSlug: string
    agentName: string
    queries: number
    avgLatencyMs: number
    errorRate: number
    inputTokens: number
    outputTokens: number
    costUsd: number
  }>
  costTimeSeries: Array<{
    timestamp: string
    inputTokens: number
    outputTokens: number
    costUsd: number
    queries: number
  }>
  toolUsage: Array<{
    tool: string
    callCount: number
    errorCount: number
  }>
  errorBreakdown: Array<{
    category: string
    count: number
  }>
}

function formatBucketLabel(timestamp: string, range: '24h' | '7d' | '30d'): string {
  const d = new Date(timestamp)
  if (range === '24h') {
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleString([], { month: 'short', day: 'numeric' })
}

export default function AiOverviewPage(): JSX.Element {
  const toast = useToast()
  const [range, setRange] = useState<'24h' | '7d' | '30d'>('24h')
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState<null | 'csv' | 'json'>(null)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)

  const handleExport = useCallback(
    async (format: 'csv' | 'json') => {
      setExportMenuOpen(false)
      setExporting(format)
      try {
        const res = await fetch(
          `/api/admin/ai/overview/export?range=${range}&format=${format}`,
          { credentials: 'include' },
        )
        if (!res.ok) throw new Error(`Export failed (${res.status})`)
        const blob = await res.blob()
        const disposition = res.headers.get('Content-Disposition') ?? ''
        const match = disposition.match(/filename="([^"]+)"/)
        const filename = match?.[1] ?? `ai-center-${range}.${format}`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } catch (err) {
        toast.error('Export failed', err instanceof Error ? err.message : String(err))
      } finally {
        setExporting(null)
      }
    },
    [range, toast],
  )

  const load = useCallback(async (selectedRange: string) => {
    setLoading(true)
    try {
      const res = await api.get<OverviewData>(`/admin/ai/overview?range=${selectedRange}`)
      setData(res)
    } catch (err) {
      toast.error('Failed to load dashboard metrics', String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void load(range)
  }, [range])

  const volumeChart = useMemo(() => {
    if (!data || data.latencyTimeSeries.length === 0) return null
    return {
      labels: data.latencyTimeSeries.map((p) => formatBucketLabel(p.timestamp, range)),
      datasets: [
        {
          label: 'Queries',
          backgroundColor: CHART_BLUE,
          borderRadius: 3,
          data: data.latencyTimeSeries.map((p) => p.queries),
        },
      ],
    }
  }, [data])

  const latencyChart = useMemo(() => {
    if (!data || data.latencyTimeSeries.length === 0) return null
    const points = data.latencyTimeSeries
    return {
      labels: points.map((p) => formatBucketLabel(p.timestamp, range)),
      datasets: [
        {
          label: 'Avg latency (ms)',
          data: points.map((p) => p.avgLatencyMs),
          borderColor: CHART_BLUE,
          backgroundColor: 'rgba(52,84,207,0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: points.map((p) => (p.avgLatencyMs > 500 ? CHART_AMBER : CHART_BLUE)),
        },
        {
          label: '500ms target',
          data: points.map(() => 500),
          borderColor: CHART_AMBER,
          borderDash: [4, 4],
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
        },
      ],
    }
  }, [data])

  const channelChart = useMemo(() => {
    if (!data || data.channelUsage.length === 0) return null
    return {
      labels: data.channelUsage.map((c) => c.channel.replace('_', ' ')),
      datasets: [
        {
          data: data.channelUsage.map((c) => c.queries),
          backgroundColor: [CHART_BLUE, CHART_AMBER, CHART_EMERALD, '#8b5cf6', '#ec4899', '#64748b'],
        },
      ],
    }
  }, [data])

  const costChart = useMemo(() => {
    if (!data || data.costTimeSeries.length === 0) return null
    const points = data.costTimeSeries
    return {
      labels: points.map((p) => formatBucketLabel(p.timestamp, range)),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Input tokens',
          backgroundColor: CHART_BLUE,
          data: points.map((p) => p.inputTokens),
          yAxisID: 'y',
          stack: 'tokens',
        },
        {
          type: 'bar' as const,
          label: 'Output tokens',
          backgroundColor: CHART_EMERALD,
          data: points.map((p) => p.outputTokens),
          yAxisID: 'y',
          stack: 'tokens',
        },
        {
          type: 'line' as const,
          label: 'Cost (USD)',
          borderColor: CHART_AMBER,
          backgroundColor: CHART_AMBER,
          data: points.map((p) => p.costUsd),
          yAxisID: 'cost',
          tension: 0.3,
          pointRadius: 2,
        },
      ],
    }
  }, [data])

  const costChartOptions = useMemo(
    () => ({
      ...BASE_CHART_OPTIONS,
      scales: {
        ...BASE_CHART_OPTIONS.scales,
        y: { ...BASE_CHART_OPTIONS.scales.y, stacked: true, title: { display: true, text: 'Tokens', font: CHART_FONT } },
        cost: {
          position: 'right' as const,
          beginAtZero: true,
          grid: { drawOnChartArea: false },
          ticks: { font: CHART_FONT, callback: (v: unknown) => `$${Number(v).toFixed(2)}` },
          title: { display: true, text: 'Cost (USD)', font: CHART_FONT },
        },
      },
    }),
    [],
  )

  return (
    <RequirePermission permission="admin:extensions:view">
      <div className="space-y-5">
        <div className="flex justify-end gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportMenuOpen((o) => !o)}
              disabled={exporting !== null}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? `Exporting ${exporting.toUpperCase()}…` : 'Export'}
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  onClick={() => void handleExport('csv')}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                >
                  Download CSV
                </button>
                <button
                  type="button"
                  onClick={() => void handleExport('json')}
                  className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                >
                  Download JSON
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
            {(['24h', '7d', '30d'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                  range === r ? 'bg-[#3454cf] text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        {data && (
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Total AI Queries</span>
                <Activity className="h-4 w-4 text-[#3454cf]" />
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{data.summary.totalQueries.toLocaleString()}</p>
              {data.summary.queryTrendPercent !== undefined && (
                <span className={`text-[10px] ${data.summary.queryTrendPercent >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {data.summary.queryTrendPercent >= 0 ? '↑' : '↓'} {Math.abs(data.summary.queryTrendPercent)}% vs previous period
                </span>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Avg Latency</span>
                <Zap className="h-4 w-4 text-amber-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">{data.summary.avgLatencyMs} ms</p>
              <span className={`text-[10px] ${data.summary.avgLatencyMs < 500 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {data.summary.avgLatencyMs < 500 ? 'Within target' : 'Above 500ms target'}
              </span>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Est. Model Spend</span>
                <DollarSign className="h-4 w-4 text-emerald-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-gray-900">${data.summary.estimatedCostUsd.toFixed(2)}</p>
              <span className="text-[10px] text-gray-400">
                {((data.summary.totalInputTokens + data.summary.totalOutputTokens) / 1000).toFixed(0)}k tokens
                {data.summary.costTrendPercent !== undefined && (
                  <span className={`ml-1 ${data.summary.costTrendPercent >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    ({data.summary.costTrendPercent >= 0 ? '↑' : '↓'}{Math.abs(data.summary.costTrendPercent)}%)
                  </span>
                )}
              </span>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>KB Training Hit Rate</span>
                <Target className="h-4 w-4 text-purple-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-emerald-600">{data.summary.trainingHitRate}%</p>
              <span className="text-[10px] text-gray-400">Q&A match rate</span>
            </div>
          </div>
        )}

        {/* Main Grid: Model Breakdown & Channel Usage */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section icon={<Bot className="h-4 w-4" />} title="Model & Provider Performance">
            {loading || !data ? (
              <p className="py-6 text-center text-xs text-gray-400">Loading model performance stats…</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 font-semibold text-gray-500">
                      <th className="pb-2">Provider & Model</th>
                      <th className="pb-2 text-right">Queries</th>
                      <th className="pb-2 text-right">Avg Latency</th>
                      <th className="pb-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.modelBreakdown.map((m, idx) => (
                      <tr key={`${m.provider}-${m.model}-${idx}`}>
                        <td className="py-2.5 font-medium text-gray-900">
                          <span className="capitalize text-gray-500">{m.provider}:</span> {m.model}
                        </td>
                        <td className="py-2.5 text-right font-mono text-gray-700">{m.queries.toLocaleString()}</td>
                        <td className="py-2.5 text-right font-mono text-gray-700">{m.avgLatencyMs}ms</td>
                        <td className="py-2.5 text-right font-mono text-emerald-600">${m.costUsd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section icon={<TrendingUp className="h-4 w-4" />} title="Channel Traffic Distribution">
            {loading || !data ? (
              <p className="py-6 text-center text-xs text-gray-400">Loading traffic stats…</p>
            ) : data.channelUsage.length === 0 ? (
              <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="relative mx-auto h-44 w-44">
                  {channelChart && (
                    <Chart
                      type="doughnut"
                      data={channelChart}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom', labels: { font: CHART_FONT, boxWidth: 12 } } },
                      }}
                      className="h-full w-full"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {data.channelUsage.map((c) => (
                    <div key={c.channel} className="flex justify-between text-xs font-medium text-gray-700">
                      <span className="capitalize">{c.channel.replace('_', ' ')}</span>
                      <span className="tabular-nums">{c.percentage}% ({c.queries.toLocaleString()})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* AI Query Volume */}
        <Section icon={<BarChart3 className="h-4 w-4" />} title="AI Query Volume">
          {loading || !data ? (
            <p className="py-6 text-center text-xs text-gray-400">Loading query volume…</p>
          ) : !volumeChart ? (
            <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
          ) : (
            <div style={{ height: '220px' }}>
              <Chart type="bar" data={volumeChart} options={BASE_CHART_OPTIONS} />
            </div>
          )}
        </Section>

        {/* Latency Trend */}
        <Section icon={<Zap className="h-4 w-4" />} title="Latency Trend">
          {loading || !data ? (
            <p className="py-6 text-center text-xs text-gray-400">Loading latency trend…</p>
          ) : !latencyChart ? (
            <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
          ) : (
            <div style={{ height: '220px' }}>
              <Chart type="line" data={latencyChart} options={BASE_CHART_OPTIONS} />
            </div>
          )}
        </Section>

        {/* Cost & Token Trend */}
        <Section icon={<DollarSign className="h-4 w-4" />} title="Cost & Token Trend">
          {loading || !data ? (
            <p className="py-6 text-center text-xs text-gray-400">Loading cost trend…</p>
          ) : !costChart ? (
            <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
          ) : (
            <>
              {data.summary.costTrendPercent !== undefined && (
                <p className="mb-2 text-xs text-gray-500">
                  Spend{' '}
                  <span className={data.summary.costTrendPercent >= 0 ? 'text-amber-600' : 'text-emerald-600'}>
                    {data.summary.costTrendPercent >= 0 ? '↑' : '↓'}{Math.abs(data.summary.costTrendPercent)}%
                  </span>{' '}
                  vs previous period
                </p>
              )}
              <div style={{ height: '240px' }}>
                <Chart type="bar" data={costChart} options={costChartOptions} />
              </div>
            </>
          )}
        </Section>

        {/* Per-Agent Performance */}
        <Section icon={<Bot className="h-4 w-4" />} title="Per-Agent Performance">
          {loading || !data ? (
            <p className="py-6 text-center text-xs text-gray-400">Loading per-agent stats…</p>
          ) : data.agentBreakdown.length === 0 ? (
            <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 font-semibold text-gray-500">
                    <th className="pb-2">Agent</th>
                    <th className="pb-2 text-right">Queries</th>
                    <th className="pb-2 text-right">Avg Latency</th>
                    <th className="pb-2 text-right">Error Rate</th>
                    <th className="pb-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.agentBreakdown.map((a) => {
                    const badge =
                      a.errorRate < 2
                        ? 'bg-emerald-100 text-emerald-700'
                        : a.errorRate < 10
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    return (
                      <tr key={a.agentSlug}>
                        <td className="py-2.5 font-medium text-gray-900">{a.agentName}</td>
                        <td className="py-2.5 text-right font-mono tabular-nums text-gray-700">{a.queries.toLocaleString()}</td>
                        <td className="py-2.5 text-right font-mono tabular-nums text-gray-700">{a.avgLatencyMs}ms</td>
                        <td className="py-2.5 text-right">
                          <span className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ${badge}`}>
                            {a.errorRate.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-2.5 text-right font-mono tabular-nums text-emerald-600">${a.costUsd.toFixed(2)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Tool Usage & Errors */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Section icon={<Wrench className="h-4 w-4" />} title="Tool Usage">
            {loading || !data ? (
              <p className="py-6 text-center text-xs text-gray-400">Loading tool usage…</p>
            ) : data.toolUsage.length === 0 ? (
              <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
            ) : (
              <div className="flex flex-col gap-2">
                {data.toolUsage.map((t) => (
                  <div key={t.tool} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 p-2.5 text-xs">
                    <span className="font-medium text-gray-900">{t.tool}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono tabular-nums text-gray-600">{t.callCount.toLocaleString()} calls</span>
                      {t.errorCount > 0 && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[11px] font-bold text-red-700">
                          {t.errorCount} failed
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section icon={<AlertTriangle className="h-4 w-4" />} title="Error Breakdown">
            {loading || !data ? (
              <p className="py-6 text-center text-xs text-gray-400">Loading error breakdown…</p>
            ) : data.errorBreakdown.length === 0 ? (
              <p className="py-6 text-center text-xs text-gray-400">No data for this range</p>
            ) : (
              <div className="flex flex-col gap-2">
                {data.errorBreakdown.map((e) => (
                  <div key={e.category} className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs">
                    <span className="font-semibold text-gray-900">{e.category}</span>
                    <span className="rounded bg-amber-200 px-2 py-0.5 font-mono text-[11px] font-bold text-amber-900">
                      {e.count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Top Errors Section */}
        {data && (
          <Section icon={<AlertTriangle className="h-4 w-4" />} title="Top Failing AI Workloads">
            <div className="flex flex-col gap-2">
              {data.topFailingQueries.map((f, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs">
                  <div>
                    <span className="font-semibold text-gray-900">"{f.query}"</span>
                    <p className="mt-0.5 text-amber-800">Error: {f.error}</p>
                  </div>
                  <span className="rounded bg-amber-200 px-2 py-0.5 font-mono text-[11px] font-bold text-amber-900">
                    {f.count} failures
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </RequirePermission>
  )
}
